import { Link, useRouterState } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { navItemsFor, resolveActivePath } from '@/components/app-nav-items';
import { useNavEligibility } from '@/hooks/use-nav-eligibility';
import { cn, createTestIdHelper } from '@/lib/utils';

const tabBarTestId = createTestIdHelper('mobile-tab-bar');

/**
 * Bottom tab navigation, shown below `lg` where the sidebar is hidden.
 *
 * Stateless by design: four or five stable destinations render as plain links,
 * so there is no open/close state, scrim, scroll lock or focus trap to manage.
 * Height comes from `--mobile-tab-bar-h` so the root padding and the sticky
 * save bars in patient details stay in agreement with it.
 */
export function MobileTabBar(): React.JSX.Element {
  const { t } = useTranslation('common');
  const activePath = useRouterState({
    select: (state) =>
      resolveActivePath(
        state.location.pathname,
        (state.location.search as { from?: string }).from,
      ),
  });

  // The same derivation the sidebar and the QA route read (ADR 0328), so the
  // tab bar can never offer a workspace the sidebar withholds.
  const eligibility = useNavEligibility();
  const items = navItemsFor(eligibility);

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-[var(--mobile-tab-bar-h)] border-t border-border bg-card lg:hidden"
      aria-label={t('nav.main')}
      data-testid={tabBarTestId()}
    >
      {items.map((item) => {
        const isActive = activePath.startsWith(item.to);
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            className={cn(
              'flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
              isActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid={`mobile-tab-${item.id}`}
          >
            <item.icon aria-hidden className="size-5" />
            {t(item.labelKey)}
          </Link>
        );
      })}
    </nav>
  );
}
